import { useState, useCallback } from 'react';

interface UseApiMutationOptions<TResult> {
  onSuccess?: (data: TResult) => void;
  onError?: (error: string) => void;
}

interface UseApiMutationResult<TInput, TResult> {
  mutate: (input: TInput) => Promise<TResult | undefined>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for imperative mutations (POST/PUT/DELETE).
 * Replaces repeated try/catch + setLoading patterns in action handlers.
 *
 * @param mutationFn  Async function that performs the mutation
 * @param options     Optional success/error callbacks
 */
export function useApiMutation<TInput, TResult = void>(
  mutationFn: (input: TInput) => Promise<TResult>,
  options: UseApiMutationOptions<TResult> = {}
): UseApiMutationResult<TInput, TResult> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (input: TInput): Promise<TResult | undefined> => {
      try {
        setLoading(true);
        setError(null);
        const result = await mutationFn(input);
        options.onSuccess?.(result);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten';
        setError(message);
        options.onError?.(message);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [mutationFn, options.onSuccess, options.onError] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { mutate, loading, error, reset };
}
